import React, { useMemo, useRef } from "react";
import { Form, Input, Button } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";

interface AlternativeNameItem {
  id?: string | number;
  alternativeName: string;
}

interface AlternativeNameProps {
  form: any;
  disabled?: boolean;
}

const AlternativeNameInput: React.FC<AlternativeNameProps> = ({ form, disabled }) => {
  const listRef = useRef<HTMLDivElement>(null);

  const alternativeNames = Form.useWatch("alternativeNames", form);
  const supplierName = Form.useWatch("supplierName", form);

  const duplicateNames = useMemo(() => {
    const allNames = alternativeNames || [];
    const nameCounts: Record<string, number> = {};
    allNames.forEach((item: AlternativeNameItem) => {
      const name = item?.alternativeName?.trim();
      if (name) nameCounts[name] = (nameCounts[name] || 0) + 1;
    });
    const supplierNameTrimmed = supplierName?.trim().toLowerCase();
    if (supplierNameTrimmed) {
      allNames.forEach((item: AlternativeNameItem) => {
        if (
          item?.alternativeName?.trim().toLowerCase() === supplierNameTrimmed
        ) {
          nameCounts[item.alternativeName.trim()] = 2;
        }
      });
    }
    return new Set(Object.keys(nameCounts).filter((name) => nameCounts[name] > 1));
  }, [alternativeNames, supplierName]);

  return (
    <Form.List
      name="alternativeNames"
      rules={[
        {
          validator: async (_, names) => {
            if (!names) return;
            const nameList = names.map((item: AlternativeNameItem) => item?.alternativeName?.trim()).filter(Boolean);
            if (nameList.length !== new Set(nameList).size) {
              return Promise.reject(new Error("Alternative names must be unique."));
            }
          },
        },
      ]}
    >
      {(fields, { add, remove }, { errors }) => (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontWeight: 500 }}>Alternative Supplier Names</span>
            <Button
              icon={<PlusOutlined />}
              size="small"
              onClick={() => {
                add();
                setTimeout(() => {
                  if (listRef.current) {
                    listRef.current.scrollTop = listRef.current.scrollHeight;
                  }
                }, 100);
              }}
              disabled={disabled}
              aria-label="Add new alternative name"
              style={{
                backgroundColor: "#E6F0FF",
                color: "#1677ff",
                border: "none",
                borderRadius: 6,
                fontWeight: 500,
              }}
            >
              Add New
            </Button>
          </div>
          <div
            ref={listRef}
            style={{
              border: errors.length > 0 ? "1px solid #ff4d4f" : "1px solid #e5e7eb",
              borderRadius: 8,
              padding: 12,
              background: "#fff",
              transition: "border-color 0.2s",
              minHeight: 200,
              maxHeight: 200,
              marginTop: 24,
              overflowY: "auto",
            }}
          >
            {fields.length === 0 ? (
              <div
                style={{
                  border: "1px dashed #bdbdbd",
                  borderRadius: 6,
                  padding: "16px 8px",
                  color: "#757575",
                  textAlign: "center",
                  background: "#f9fafb",
                }}
              >
                No alternative names added yet. Click "Add Alternative Name" to get started.
              </div>
            ) : (
              fields.map(({ key, name, ...restField }) => {
                const allNames = form.getFieldValue("alternativeNames") || [];
                const currentName = allNames[name]?.alternativeName?.trim();
                const isDuplicate = !!currentName && duplicateNames.has(currentName);

                return (
                  <div key={key} style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                    <Form.Item {...restField} name={[name, "id"]} style={{ display: "none" }}>
                      <Input type="hidden" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "alternativeName"]}
                      rules={[{ required: true, message: "Please input an alternative name" }]}
                      style={{
                        flex: 1,
                        marginBottom: 0,
                        ...(isDuplicate && { border: "1px solid red", borderRadius: 4 }),
                      }}
                    >
                      <Input
                        placeholder=""
                        disabled={disabled}
                        onChange={() => {
                          form.validateFields(["alternativeNames"]);
                        }}
                      />
                    </Form.Item>
                    <Button
                      type="text"
                      icon={<DeleteOutlined style={{ color: "red" }} />}
                      onClick={() => remove(name)}
                      style={{ marginLeft: 8 }}
                      disabled={disabled}
                      aria-label="Remove this alternative name"
                    />
                  </div>
                );
              })
            )}
            {errors.length > 0 && (
              <div style={{ color: "red", marginTop: 4 }}>
                {errors.map((err, idx) => (
                  <div key={idx}>{err}</div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </Form.List>
  );
};

export default AlternativeNameInput;